import { useState, useEffect, useCallback } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import {
  fetchAttributeDefinitions,
  addAttributeDefinition,
  updateAttributeDefinition,
  removeAttributeDefinition,
  fetchEnumOptions,
  addEnumOption,
  removeEnumOption,
  fetchUnitsOfMeasure,
} from "./declrAttributeDefinitionInteractionService";

const VALUE_DATA_TYPES = ["numeric", "boolean", "enum", "date", "text"];

const emptyForm = {
  Label: "",
  MetaDescription: "",
  ValueDataType: "numeric",
  DefaultUnitCode: "",
  AllowedUnitFamily: "",
  CategoryTag: "",
};

/**
 * 🎯 ATTRIBUTE DEFINITION CONTROLLER
 * Group-ESG-persona CRUD over the global, reusable local-capture attribute
 * registry (Corp.Z_10_AttributeDefinition), plus inline enum-option
 * management for attributes with ValueDataType = 'enum'.
 */
export default function DeclrAttributeDefinitionController(siteKey) {
  const [definitions, setDefinitions] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const [expandedId, setExpandedId] = useState(null);
  const [enumOptions, setEnumOptions] = useState([]);
  const [newOption, setNewOption] = useState({ OptionLabel: "", OptionValue: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [defs, unitRows] = await Promise.all([
        fetchAttributeDefinitions(null, { siteKey }),
        fetchUnitsOfMeasure(null, { siteKey }),
      ]);
      setDefinitions(defs || []);
      setUnits(unitRows || []);
    } finally {
      setLoading(false);
    }
  }, [siteKey]);

  useEffect(() => {
    load();
  }, [load]);

  const unitFamilies = [...new Set(units.map((u) => GoldenSourceHelper.get(u, "UnitFamily")))];
  const unitsForFamily = (family) => units.filter((u) => GoldenSourceHelper.get(u, "UnitFamily") === family);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = (def) => {
    setEditingId(GoldenSourceHelper.get(def, "AttributeDefinitionId"));
    setForm({
      Label: GoldenSourceHelper.get(def, "Label") || "",
      MetaDescription: GoldenSourceHelper.get(def, "MetaDescription") || "",
      ValueDataType: GoldenSourceHelper.get(def, "ValueDataType") || "numeric",
      DefaultUnitCode: GoldenSourceHelper.get(def, "DefaultUnitCode") || "",
      AllowedUnitFamily: GoldenSourceHelper.get(def, "AllowedUnitFamily") || "",
      CategoryTag: GoldenSourceHelper.get(def, "CategoryTag") || "",
    });
  };

  const handleSave = async () => {
    if (!form.Label.trim() || !form.ValueDataType) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        Label: form.Label.trim(),
        MetaDescription: form.MetaDescription || null,
        ValueDataType: form.ValueDataType,
        DefaultUnitCode: form.DefaultUnitCode || null,
        AllowedUnitFamily: form.AllowedUnitFamily || null,
        CategoryTag: form.CategoryTag || null,
      };
      if (editingId) {
        await updateAttributeDefinition(editingId, payload, { siteKey });
      } else {
        await addAttributeDefinition(payload, { siteKey });
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || "Could not save attribute definition.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (attributeDefinitionId) => {
    setError("");
    try {
      await removeAttributeDefinition(attributeDefinitionId, { siteKey });
      if (editingId === attributeDefinitionId) resetForm();
      await load();
    } catch (err) {
      setError(err.message || "Could not remove attribute definition.");
    }
  };

  const toggleEnumOptions = async (attributeDefinitionId) => {
    if (expandedId === attributeDefinitionId) {
      setExpandedId(null);
      setEnumOptions([]);
      return;
    }
    setExpandedId(attributeDefinitionId);
    const options = await fetchEnumOptions(attributeDefinitionId, { siteKey });
    setEnumOptions(options || []);
  };

  const handleAddEnumOption = async (attributeDefinitionId) => {
    if (!newOption.OptionLabel.trim() || !newOption.OptionValue.trim()) return;
    try {
      const options = await addEnumOption(
        attributeDefinitionId,
        newOption.OptionLabel.trim(),
        newOption.OptionValue.trim(),
        enumOptions.length,
        { siteKey }
      );
      setEnumOptions(options || []);
      setNewOption({ OptionLabel: "", OptionValue: "" });
    } catch (err) {
      setError(err.message || "Could not add enum option.");
    }
  };

  const handleRemoveEnumOption = async (optionId, attributeDefinitionId) => {
    try {
      await removeEnumOption(optionId, { siteKey });
      const options = await fetchEnumOptions(attributeDefinitionId, { siteKey });
      setEnumOptions(options || []);
    } catch (err) {
      setError(err.message || "Could not remove enum option.");
    }
  };

  return {
    definitions,
    units,
    unitFamilies,
    unitsForFamily,
    loading,
    saving,
    error,
    form,
    editingId,
    VALUE_DATA_TYPES,
    handleFieldChange,
    handleEdit,
    handleSave,
    handleRemove,
    resetForm,
    expandedId,
    enumOptions,
    newOption,
    setNewOption,
    toggleEnumOptions,
    handleAddEnumOption,
    handleRemoveEnumOption,
  };
}
